import React, {SyntheticEvent, useState} from 'react';

import {
  Breakpoints,
  Checkbox,
  CTA,
  Select,
  styled,
  TextInput,
  Richtext
} from '@volkswagen-onehub/components-core';
import {useAppDispatch} from '../../store/hooks';
import {set} from '../../store/features/pages/pageSlice';
import {Page} from '../../types/enums/page.enum';
import {TAOS, TIGUAN, ID4, ATLAS, ATLASCROSSPORT} from '../../data/models';
import {LeadService} from '../../services/LeadService';
import useTrans from '../../hooks/use-trans';
import {CTAWrapper} from '../../styles/global.style';
import {useFeatureServices} from '../../use-feature-services';
import {Language} from '../../types/locale';
import {isValidEmail, validatePostalCode} from '../../validation';

const FormsContainer = styled.form`
  display: flex;
  flex-wrap: wrap;
  column-gap: 5rem;
  row-gap: ${(props) => props.theme.size.dynamic0040};
  flex-direction: column;

  width: 100%;
  margin: ${(props) => props.theme.size.grid002} auto 0;
  padding: 0 ${(props) => props.theme.size.grid001};

  @media screen and (min-width: ${Breakpoints.b960}px) {
    padding: 0 ${(props) => props.theme.size.grid002} 0;
    flex-direction: row;
  }
`;

const FieldWrapper = styled.div<{fullWidth?: boolean; hasError?: boolean}>`
  flex: ${({fullWidth}) => (fullWidth ? '1 1 100%' : '1 1 calc(50% - 5rem)')};
  margin-bottom: ${(props) => props.theme.size.dynamic0150};

  div[class^='StyledTextComponent'] {
    display: block;
  }

  p[class^='StyledSmallLabelText'],
  span[class^='StyledLabelText'] {
    font-size: 1rem !important;
    color: #000 !important;
    overflow: unset;
  }

  label {
    border-radius: unset;
    padding: 0;
    box-shadow: unset;
    row-gap: ${(props) => props.theme.size.dynamic0020};

    div[class^='StyledInputLineWrapper'] {
      padding: 0;
      box-shadow: unset;

      span[class^='StyledSmallLabelText'] {
        font-size: 1rem;
        color: #000;
      }
    }

    svg {
      display: none;
    }

    &:focus-within,
    &:hover {
      box-shadow: none;
    }
  }

  input {
    border-bottom: 1px solid #000;
    padding-top: 8px;
    ${({hasError}) => hasError && 'border-bottom: 3px solid red;'}

    &:focus-within {
      box-shadow: rgb(0, 64, 197) 0px 0px 0px 1px;
    }
  }

  select {
    border-bottom: 1px solid #000;
    padding-top: 10px;

    ${({hasError}) => hasError && 'border-bottom: 3px solid red;'}
    padding-bottom: 3px;

    &:focus-within {
      box-shadow: rgb(0, 64, 197) 0px 0px 0px 1px;
    }
  }

  &:nth-child(5),
  &:nth-child(6) {
    span[class^='StyledIconWrapper'] {
      top: 25px;
      right: 0px;
    }

    svg {
      display: block !important;
    }
  }

  div[class^='StyledDisplayTag'] {
    margin-top: 12px;
  }
`;

const ActionsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: ${(props) => props.theme.size.dynamic0020} 0
    ${(props) => props.theme.size.dynamic0150};

  @media screen and (min-width: 480px) {
    flex-direction: row;
  }
`;

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  model: string;
  postal: string;
  optin: string;
};

export default function () {
  const {'locale-service': localeService} = useFeatureServices();

  const {tracking} = useFeatureServices();

  const trans = useTrans();

  const enTrans = useTrans('en' as Language);

  const dispatch = useAppDispatch();

  const [values, setValues] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    model: '',
    postal: '',
    optin: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const [requesting, setRequesting] = useState<boolean>(false);

  const update = (
    e:
      | SyntheticEvent<HTMLInputElement, Event>
      | SyntheticEvent<HTMLSelectElement, Event>,
  ): void => {

    const {name, value} = e.currentTarget;

    if(name != 'optin') {
      setValues((prev) => ({...prev, [name]: value}));
    }
    else {
      const optinValue = (e.currentTarget as HTMLInputElement).checked ? 'true' : '';
      setValues((prev) => ({...prev, [name]: optinValue}));
    }

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({...prev, [name]: undefined}));
    }
  };

  const validate = () => {
    const newErrors: Partial<FormData> = {};

    if (!values.firstName.trim())
      newErrors.firstName = trans.pages.forms.errors.firstName;
    if (!values.lastName.trim())
      newErrors.lastName = trans.pages.forms.errors.lastName;
    if (!values.email.trim()) {
      newErrors.email = trans.pages.forms.errors.email;
    } else if (!isValidEmail(values.email)) {
      newErrors.email = trans.pages.forms.errors.email;
    }
    if (!values.postal.trim()) {
      newErrors.postal = trans.pages.forms.errors.postal;
    } else if (!validatePostalCode(values.postal)) {
      newErrors.postal = trans.pages.forms.errors.postal;
    }
    if (!values.model.trim()) newErrors.model = trans.pages.forms.errors.model;
    if (!values.optin.trim()) newErrors.optin = trans.pages.forms.errors.optin;
    return newErrors;
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const errors = validate();
    setErrors(errors);

    if (Object.keys(errors).length === 0) {
      try {
        setRequesting(true);

        await LeadService.sendLead(values, localeService.language);

        setValues({
          firstName: '',
          lastName: '',
          email: '',
          model: '',
          postal: '',
          optin: '',
        });

        setErrors({});
        dispatch(set(Page.RESULT));

        setRequesting(false);

        tracking.track('VWBasic_Button_Click', {
          link: {
            url: window.location.href,
            name: enTrans.pages.forms.submit,
          },
        });
      } catch (err: any) {
        setRequesting(false);

        tracking.track('VWBasic_Button_Click', {
          link: {
            url: window.location.href,
            name: `${enTrans.pages.forms.submit} - Error`,
          },
        });
        console.log('err', err);
      }
    }
  }

  const handleSkip = () => {
    dispatch(set(Page.RESULT));

    tracking.track('VWBasic_Button_Click', {
      link: {
        url: window.location.href,
        name: enTrans.pages.forms.skip,
      },
    });
  };

  return (
    <FormsContainer onSubmit={handleSubmit}>
      <FieldWrapper hasError={errors.firstName ? true : false}>
        <TextInput
          appearance={errors.firstName ? 'error' : undefined}
          label={trans.pages.forms.fields.firstName}
          message={errors.firstName}
          name="firstName"
          onChange={(e) => update(e)}
        />
      </FieldWrapper>

      <FieldWrapper hasError={errors.lastName ? true : false}>
        <TextInput
          appearance={errors.lastName ? 'error' : undefined}
          label={trans.pages.forms.fields.lastName}
          message={errors.lastName}
          name="lastName"
          onChange={(e) => update(e)}
        />
      </FieldWrapper>

      <FieldWrapper hasError={errors.email ? true : false}>
        <TextInput
          appearance={errors.email ? 'error' : undefined}
          label={trans.pages.forms.fields.email}
          message={errors.email}
          name="email"
          onChange={(e) => update(e)}
        />
      </FieldWrapper>

      <FieldWrapper hasError={errors.postal ? true : false}>
        <TextInput
          appearance={errors.postal ? 'error' : undefined}
          label={trans.pages.forms.fields.postal}
          message={errors.postal}
          name="postal"
          onChange={(e) => update(e)}
        />
      </FieldWrapper>

      <FieldWrapper hasError={errors.model ? true : false}>
        <Select
          message={errors.model}
          appearance={errors.model ? ('error' as any) : (undefined as any)}
          name="model"
          label={trans.pages.forms.fields.model}
          onChange={(e) => update(e)}
        >
          <option></option>
          <option value={TAOS.code}>{TAOS.modelName}</option>
          <option value={ID4.code}>{ID4.modelName}</option>
          <option value={TIGUAN.code}>{TIGUAN.modelName}</option>
          <option value={ATLASCROSSPORT.code}>
            {ATLASCROSSPORT.modelName}
          </option>
          <option value={ATLAS.code}>{ATLAS.modelName}</option>
        </Select>
      </FieldWrapper>

      <FieldWrapper fullWidth>
        <Checkbox
          onChange={(e) => update(e)}
          name="optin"
          value="true"
          label={<Richtext markdown={trans.pages.forms.fields.optin} dangerouslyParseRawHTML />}
          appearance={errors.optin ? ('error' as any) : (undefined as any)}
          message={errors.optin as unknown as string}
        />
      </FieldWrapper>

      <FieldWrapper />

      <ActionsWrapper>
        <CTAWrapper>
          <CTA
            tag="button"
            emphasis="primary"
            type="submit"
            disabled={requesting}
          >
            {trans.pages.forms.submit}
          </CTA>
        </CTAWrapper>
        <CTAWrapper>
          <CTA
            tag="button"
            emphasis="secondary"
            type="button"
            onClick={handleSkip}
          >
            {trans.pages.forms.skip}
          </CTA>
        </CTAWrapper>
      </ActionsWrapper>
    </FormsContainer>
  );
}
